import { NumberInputRow } from "./NumberInputRow"
import { SegmentedCountRow } from "./SegmentedCountRow"
import style from "./AttachmentsSection.module.scss"

const { section, sectionTitle } = style

const YES_NO_OPTIONS = [
	{ label: "Yes", value: "yes" as const },
	{ label: "No", value: "no" as const },
]

/** Documented ceiling for `adjusted_shelves` on the configuration endpoints (integer in [0, 10]). */
const ADJUSTABLE_SHELVES_MAX = 10

/** Documented ceiling for `grommets` / `wall_cleats` on the countertop-attachments endpoints (integer in [0, 20]). */
const COUNTERTOP_ATTACHMENT_MAX = 20

const COUNT_OPTIONS = [
	{ label: "0", value: 0 },
	{ label: "1", value: 1 },
	{ label: "2", value: 2 },
]

/**
 * `null` on any of the four cabinet values means "the selected objects disagree" — the batch panel
 * (REQ-403) passes it so the segmented rows render with NO segment active and the shelves input
 * renders empty with its `Mixed` placeholder. The single-object sidebar never passes null for the
 * three non-nullable ones.
 */
type CabinetProps = {
	variant: "cabinet"
	adjustableShelves: number | null
	onAdjustableShelvesChange: (value: number | null) => void
	onAdjustableShelvesCommit: () => void
	hasLocks: "yes" | "no" | null
	onHasLocksChange: (value: "yes" | "no") => void
	fillings: number | null
	onFillingsChange: (value: number) => void
	finishingEnds: number | null
	onFinishingEndsChange: (value: number) => void
	/** Placeholder for the adjustable-shelves input — `Mixed` in the batch panel. */
	adjustableShelvesPlaceholder?: string
}

/**
 * REQ-449. Grommets / Wall Cleats — free-entry integer counts on a countertop, copied structurally
 * from the cabinet `NumberInputRow` fields per the issue's explicit instruction. `null` on either
 * value means "the selected objects disagree" (batch panel), same convention as the cabinet side.
 */
type CountertopProps = {
	variant: "countertop"
	grommets: number | null
	onGrommetsChange: (value: number | null) => void
	onGrommetsCommit: () => void
	wallCleats: number | null
	onWallCleatsChange: (value: number | null) => void
	onWallCleatsCommit: () => void
	/** Placeholder for both countertop-attachment inputs — `Mixed` in the batch panel. */
	countertopAttachmentsPlaceholder?: string
}

type Props = CabinetProps | CountertopProps

export const AttachmentsSection = (props: Props) => {
	if (props.variant === "countertop") {
		const { grommets, onGrommetsChange, onGrommetsCommit, wallCleats, onWallCleatsChange, onWallCleatsCommit, countertopAttachmentsPlaceholder } =
			props
		return (
			<div className={section}>
				<p className={sectionTitle}>Attachments</p>
				<NumberInputRow
					label="Grommets"
					max={COUNTERTOP_ATTACHMENT_MAX}
					value={grommets}
					onChange={onGrommetsChange}
					onBlur={onGrommetsCommit}
					placeholder={countertopAttachmentsPlaceholder}
				/>
				<NumberInputRow
					label="Wall Cleats"
					max={COUNTERTOP_ATTACHMENT_MAX}
					value={wallCleats}
					onChange={onWallCleatsChange}
					onBlur={onWallCleatsCommit}
					placeholder={countertopAttachmentsPlaceholder}
				/>
			</div>
		)
	}

	const {
		adjustableShelves,
		onAdjustableShelvesChange,
		onAdjustableShelvesCommit,
		hasLocks,
		onHasLocksChange,
		fillings,
		onFillingsChange,
		finishingEnds,
		onFinishingEndsChange,
		adjustableShelvesPlaceholder,
	} = props

	return (
		<div className={section}>
			<p className={sectionTitle}>Attachments</p>
			<SegmentedCountRow
				label="Finished ends"
				options={COUNT_OPTIONS}
				value={finishingEnds}
				onChange={onFinishingEndsChange}
			/>
			<SegmentedCountRow
				label="Fillers"
				options={COUNT_OPTIONS}
				value={fillings}
				onChange={onFillingsChange}
			/>
			<SegmentedCountRow
				label="Locks"
				required
				options={YES_NO_OPTIONS}
				value={hasLocks}
				onChange={onHasLocksChange}
			/>
			<NumberInputRow
				label="Additional adjustable shelves"
				required
				// `adjusted_shelves` is documented as an integer in [0, 10] on BOTH the single-object and
				// the batch configuration endpoints, so anything above 10 could only be rejected — and in
				// a batch it would reject every selected object.
				max={ADJUSTABLE_SHELVES_MAX}
				value={adjustableShelves}
				onChange={onAdjustableShelvesChange}
				onBlur={onAdjustableShelvesCommit}
				placeholder={adjustableShelvesPlaceholder}
			/>
		</div>
	)
}
